import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, TrendingUp } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { ImageBlock } from "../components/ImageBlock";
import { ApartmentGallery, ApartmentAbout } from "../components/ApartmentAbout";
import { BUILDINGS, getApartmentSpec } from "../data/apartments";
import { useBuildings } from "../data/ApartmentDataContext";
import { useInventory, statusColor } from "../hooks/useInventory";
import { Seo } from "../components/Seo";

export default function ApartmentDetail({ buildingKey, route }) {
  const { unit } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const inventory = useInventory();
  const { buildings } = useBuildings();
  const building = buildings[buildingKey] || BUILDINGS[buildingKey];
  const apt = building.apartments.find((a) => a.unit === unit);

  if (!apt) {
    return (
      <div className="pt-40 text-center min-h-screen">
        <p className="text-cream/60">404</p>
        <Link to={`/${route}`} className="text-gold underline mt-4 inline-block">{t("apartment.backToBuilding")}</Link>
      </div>
    );
  }

  const st = statusColor(inventory[apt.code]);
  const fmt = (n) => new Intl.NumberFormat("lt-LT").format(n);

  const breakdown = apt.roomBreakdown;
  const spec = getApartmentSpec(apt);
  const ORIENT_ORDER = ["east", "south", "west", "north"];
  const orientKeys = spec.orientation && spec.orientation.length ? spec.orientation : (apt.orientationKeys || []);
  const orientationText =
    [...orientKeys].sort((a, b) => {
      const ia = ORIENT_ORDER.indexOf(a), ib = ORIENT_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    }).map((d) => t(`spec.dir_${d}`)).join(", ") ||
    (apt.orientation && apt.orientation !== "—" ? apt.orientation : "—");
  const roomPhoto = apt.photos && apt.photos.length > 0 ? apt.photos[0] : null;
  const galleryPhotos = apt.photos && apt.photos.length > 1 ? apt.photos.slice(1) : [];
  const SPACE_WORD = { loggia: "lodžija", terrace: "terasa", balcony: "balkonu" };
  const spaceWord = SPACE_WORD[apt.terraceType] || "";
  const landShareValue = apt.landShareNA
    ? t("common.notApplicable")
    : (apt.landShare != null ? `${apt.landShare} m²` : t("common.notApplicable"));

  let keyFacts;
  if (apt.factsVariant === "loggia") {
    keyFacts = [
      { label: t("apartment.totalWithLoggia"), value: `${apt.area} m²` },
      { label: t("spec.apartmentArea"), value: `${apt.apartmentArea ?? apt.area} m²` },
      { label: t("spec.loggiaArea"), value: `${apt.loggiaArea ?? "—"} m²` },
      { label: t("apartment.landShare"), value: landShareValue },
      { label: t("common.rooms"), value: apt.rooms },
      { label: t("spec.orientation"), value: orientationText },
    ];
  } else if (apt.landShare != null || apt.landShareNA) {
    keyFacts = [
      { label: t("apartment.totalApartmentArea"), value: `${apt.apartmentArea ?? apt.area} m²` },
      { label: t("spec.terraceArea"), value: `${apt.terraceArea ?? "—"} m²` },
      { label: t("apartment.landShare"), value: landShareValue },
      { label: t("common.rooms"), value: apt.rooms },
      { label: t("spec.orientation"), value: orientationText },
    ];
  } else {
    keyFacts = [
      { label: t("apartment.totalArea"), value: `${apt.area} m²` },
      { label: t("common.rooms"), value: apt.rooms },
      { label: t("spec.orientation"), value: orientationText },
      { label: t("common.terrace"), value: apt.terraceType === "terrace" ? t("common.terraceLabel") : t("common.loggiaLabel") },
    ];
  }

  return (
    <div data-testid="apartment-detail-page" className="pt-28">
      <Seo
        title={`${apt.code} — ${apt.rooms} kamb. butas | Sakurų namai`}
        description={`${apt.code}: ${apt.rooms} kambarių, ${apt.area} m² A++ butas Sakurų namai, Parko g. 1D, Marijampolė.`}
        path={`/${route}/${apt.unit}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Apartment",
          name: `Sakurų namai — ${apt.code}`,
          numberOfRoomsTotal: apt.rooms,
          floorSize: { "@type": "QuantitativeValue", value: apt.area, unitCode: "MTK" },
          address: {
            "@type": "PostalAddress",
            streetAddress: "Parko g. 1D",
            addressLocality: "Marijampolė",
            postalCode: "68218",
            addressCountry: "LT",
          },
          ...(apt.loggiaPrice != null
            ? { offers: { "@type": "Offer", price: apt.loggiaPrice, priceCurrency: "EUR", availability: "https://schema.org/InStock" } }
            : {}),
          ...(apt.sheet ? { image: apt.sheet } : {}),
        }}
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <Link to={`/${route}`} data-testid="back-to-building" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cream/50 hover:text-gold transition-colors mb-8">
          <ArrowLeft size={14} /> {t("apartment.backToBuilding")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Floor plan (full, uncropped) + room photo stacked */}
          <div className="space-y-6">
            <div>
              {apt.sheet ? (
                <div className="bg-charcoal-light border border-gold/20 rounded-lg p-3 flex items-center justify-center">
                  <img src={apt.sheet} alt={apt.code} data-testid="apartment-sheet" className="w-full h-auto object-contain rounded" loading="lazy" />
                </div>
              ) : (
                <ImageBlock src="" alt={apt.code} className="aspect-[3/4] rounded-lg border border-gold/20" testid="apartment-sheet" />
              )}
              <p className="text-center text-xs uppercase tracking-widest text-gold/70 mt-4">{t("apartment.floorPlanSheet")} — {apt.code}</p>
            </div>
            {roomPhoto && (
              <div className="bg-charcoal-light border border-cream/10 rounded-lg p-3 flex items-center justify-center">
                <img src={roomPhoto} alt={apt.code} data-testid="apartment-room-photo" className="w-full h-auto object-contain rounded" loading="lazy" />
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className={`inline-block text-[10px] uppercase tracking-wider px-3 py-1 rounded-full border ${st.cls}`}>
                {t(`common.${st.text}`)}
              </span>
              <span className="text-xs uppercase tracking-widest text-gold/70">A++ · {building.key}</span>
            </div>
            <h1 className="font-serif text-5xl font-light text-cream">{apt.code}</h1>
            <p className="text-cream/60 mt-2">
              {apt.rooms} {t("common.rooms").toLowerCase()} · {apt.floor} {t("apartment.floorOf")} {apt.floors}
            </p>

            <div className="mt-8">
              {apt.loggiaPrice != null && (
                <>
                  <p className="font-serif text-3xl sm:text-4xl font-bold" style={{ color: "#977E2E" }} data-testid="apt-price-label">
                    {fmt(apt.loggiaPrice)} €{spaceWord ? ` buto su ${spaceWord} kaina` : ""}
                  </p>
                  {apt.loggiaPricePerM2 != null && (
                    <p className="text-sm text-cream/60 mt-2" data-testid="apt-price-subtext">
                      {fmt(apt.loggiaPricePerM2)} €/m²{spaceWord ? ` su ${spaceWord} kaina` : ""}
                    </p>
                  )}
                </>
              )}
            </div>
            {apt.priceIncrease && (
              <p className="flex items-center gap-2 text-xs text-gold/70 mt-2">
                <TrendingUp size={14} /> {t("apartment.priceIncrease").replace("{x}", apt.priceIncrease)}
              </p>
            )}

            {/* Key facts */}
            <div className="grid grid-cols-2 gap-px bg-cream/10 mt-10 rounded-lg overflow-hidden border border-cream/10">
              {keyFacts.map((f, i) => (
                <div key={i} className="bg-charcoal-light p-5">
                  <p className="text-[11px] uppercase tracking-widest text-cream/40">{f.label}</p>
                  <p className="text-cream mt-1">{f.value}</p>
                </div>
              ))}
            </div>

            {/* Room breakdown */}
            {breakdown && (
              <div className="mt-8">
                <h3 className="text-xs uppercase tracking-[0.25em] text-gold/70 mb-4">{t("apartment.breakdown")}</h3>
                <table className="w-full text-sm border border-cream/10 rounded-lg overflow-hidden">
                  <tbody>
                    {breakdown.map((r, i) => (
                      <tr key={i} className="border-b border-cream/5">
                        <td className="px-4 py-3 text-cream/70">{lang === "lt" ? r.lt : lang === "ru" ? r.ru : r.en}</td>
                        <td className="px-4 py-3 text-right text-cream">{r.area} m²</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Link
              to={`/kontaktai?building=${encodeURIComponent(building.key)}&apartment=${encodeURIComponent(apt.code)}`}
              data-testid="apartment-inquire-btn"
              className="inline-block mt-10 border border-gold/50 text-gold text-xs uppercase tracking-widest px-8 py-4 hover:bg-gold/10 transition-colors"
            >
              {t("apartment.inquire")}
            </Link>
            <div className="mt-6">
              <Link to="/galerija" data-testid="apartment-gallery-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cream/50 hover:text-gold transition-colors">
                {t("nav.gallery")} <ArrowLeft size={13} className="rotate-180" />
              </Link>
            </div>
          </div>
        </div>

        {/* Photo gallery + More about the apartment */}
        {galleryPhotos.length > 0 && (
          <div className="mt-16">
            <ApartmentGallery photos={galleryPhotos} title={apt.code} />
          </div>
        )}
        <ApartmentAbout apt={apt} />
      </section>
    </div>
  );
}
